import { useState } from "react";
import { Link } from "wouter";
import { clsx } from "clsx";
import { Check, ChevronDown, FileSpreadsheet, RotateCcw } from "lucide-react";
import { Boton, Cargando, Dialogo } from "@/components/ui";
import { Barra } from "@/components/ui/controles";
import { useDatos } from "./store/store";
import { useDatosApi } from "./DatosContext";
import { SECCIONES, type Seccion } from "./secciones";
import { progresoSeccion, type Progreso } from "./progreso";
import { useFotoEnVivo } from "./useFotoEnVivo";
import { IndicadorGuardado } from "./componentes/IndicadorGuardado";
import { ResumenEnVivo } from "./componentes/PanelEnVivo";
import { GuardarFoto } from "./componentes/GuardarFoto";

function TarjetaSeccion({ s, p }: { s: Seccion; p: Progreso }) {
  const accion = p.estado === "vacio" ? "Empezar" : p.estado === "listo" ? "Revisar" : "Continuar";
  return (
    <Link href={`/datos/${s.id}`} className="group flex items-center gap-4 rounded-md border border-borde bg-white p-4 transition-colors hover:border-primary">
      <span className={clsx("flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold", p.estado === "listo" ? "border-emerald-700 bg-emerald-700 text-white" : "border-borde text-tinta-suave")} aria-hidden>
        {p.estado === "listo" ? <Check className="h-4 w-4" /> : SECCIONES.indexOf(s) + 1}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline justify-between gap-2"><span className="font-semibold">{s.titulo}</span><span className="shrink-0 text-xs text-tinta-suave">{s.tiempo}</span></span>
        <span className="text-sm text-tinta-suave">{s.queDa}</span>
        {p.estado === "medias" && <span className="mt-1 flex items-center gap-2"><Barra valor={p.done / p.total} etiqueta={`Progreso de ${s.titulo}`} /><span className="tabular shrink-0 text-xs text-tinta-suave">{p.done}/{p.total}</span></span>}
      </span>
      <span className="hidden shrink-0 text-sm font-medium text-primary sm:inline">{accion} →</span>
    </Link>
  );
}

export function MisDatos() {
  const inputs = useDatos((s) => s.inputs);
  const meta = useDatos((s) => s.meta);
  const api = useDatosApi();
  const { snapshot } = useFotoEnVivo();
  const [verTalleres, setVerTalleres] = useState<boolean | null>(null);
  const [confirmarCero, setConfirmarCero] = useState(false);
  if (api.cargando) return <Cargando texto="Cargando tus datos…" />;
  const prog = Object.fromEntries(SECCIONES.map((s) => [s.id, progresoSeccion(inputs, s.id, snapshot)])) as Record<string, Progreso>;
  const base = SECCIONES.filter((s) => s.grupo === "base"), talleres = SECCIONES.filter((s) => s.grupo === "talleres");
  const talleresConDatos = talleres.some((s) => prog[s.id].estado !== "vacio");
  const abiertos = verTalleres ?? talleresConDatos;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Mis datos</h1>
          <p className="mt-1 text-tinta-suave">Lo mismo que el Excel, por partes. Se guarda solo; tu foto se arma mientras llenas.</p>
          <div className="mt-1"><IndicadorGuardado /></div>
        </div>
        <div className="flex flex-col items-start sm:items-end"><GuardarFoto /></div>
      </div>
      {meta.avisos.length > 0 && <div className="rounded-md border border-amber-200 bg-alerta-suave p-3 text-sm text-amber-900">{meta.avisos.map((a, i) => <p key={i}>{a}</p>)}</div>}
      {meta.seedUpload && <p className="text-sm text-tinta-suave">Sembrado desde tu Excel. Corrige aquí lo que falte.</p>}
      {snapshot && (snapshot.balance.totals.assets > 0 || snapshot.flow.totals.incomeAnnual > 0) && (
        <section aria-label="Tu foto en vivo" className="md:hidden"><ResumenEnVivo s={snapshot} compacto /></section>
      )}
      <div className="grid gap-5 md:grid-cols-[7fr_5fr]">
        <div className="flex flex-col gap-3">
          <h2 className="eyebrow text-tinta-suave">Tus números (Semana 0)</h2>
          {base.map((s) => <TarjetaSeccion key={s.id} s={s} p={prog[s.id]} />)}
          <button type="button" onClick={() => setVerTalleres(!abiertos)} aria-expanded={abiertos} className="mt-2 flex min-h-11 items-center justify-between rounded-md px-1 text-left eyebrow text-tinta-suave hover:text-tinta">
            Para los talleres <ChevronDown className={clsx("h-4 w-4 transition-transform", abiertos && "rotate-180")} aria-hidden />
          </button>
          {abiertos && talleres.map((s) => <TarjetaSeccion key={s.id} s={s} p={prog[s.id]} />)}
          <div className="mt-4 flex flex-col gap-2 border-t border-borde pt-4 text-sm sm:flex-row sm:flex-wrap">
            <Link href="/datos/excel"><Boton variante="fantasma" tamano="sm"><FileSpreadsheet className="h-4 w-4" aria-hidden /> Prefiero subir mi Excel</Boton></Link>
            {api.modo === "real" && <Boton variante="fantasma" tamano="sm" onClick={() => setConfirmarCero(true)}><RotateCcw className="h-4 w-4" aria-hidden /> Empezar de cero</Boton>}
          </div>
        </div>
        <aside className="hidden md:block">
          {snapshot && <div className="sticky top-20 flex flex-col gap-2"><h2 className="text-sm font-semibold text-tinta-suave">Tu foto se va armando</h2><ResumenEnVivo s={snapshot} /></div>}
        </aside>
      </div>
      <Dialogo abierto={confirmarCero} onCerrar={() => setConfirmarCero(false)} titulo="¿Empezar de cero?">
        <p className="text-sm text-tinta-suave">Se borra este borrador. Tus fotos guardadas no se tocan.</p>
        <div className="mt-4 flex justify-end gap-2"><Boton variante="secundario" onClick={() => setConfirmarCero(false)}>Cancelar</Boton><Boton variante="peligro" onClick={() => { void api.empezarDeCero(); setConfirmarCero(false); }}>Borrar borrador</Boton></div>
      </Dialogo>
    </div>
  );
}
